"use client";

import * as React from "react";
import { Drawer } from "vaul";
import { WalletPortfolioContext } from "../providers/wallet-portfolio-provider";
import { resolveEnvironment } from "../../../lib/environment";
import type { Environment, Theme } from "../../../types";
import type { OnOutlink } from "../../../lib/outlink";
import type {
  EstimateGasFn,
  LpBalanceReaderFn,
  SendTransactionFn,
  StakingRewardsReaderFn,
} from "../types";
import { WalletPortfolioTrigger } from "./WalletPortfolioTrigger";
import { WalletPortfolioContent } from "./WalletPortfolioContent";
import "../styles/portfolio.css";

export interface WalletPortfolioProps {
  env?: Environment;
  theme?: Theme;
  walletAddress: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 포트폴리오 섹션 내부 외부 링크 이동을 가로채는 콜백.
   * 반환값으로 URL 변형 / 취소가 가능하며 async도 지원한다.
   */
  onOutlink?: OnOutlink;
  /**
   * game-swap LP 섹션이 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 dapp-ui가 CROSS 체인
   * RPC로 직접 읽는 내부 폴백으로 동작한다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션이 온체인 `earned()` / `balanceOf()`를 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 API 값으로 폴백한다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  /**
   * 내장 Withdraw(Rewards) 트랜잭션 서명/브로드캐스트 콜백.
   * wagmi의 `sendTransactionAsync`를 그대로 전달해도 호환된다.
   * 미주입 시 Withdraw는 기존 외부 링크로 폴백한다.
   */
  sendTransaction?: SendTransactionFn;
  /** 내장 Withdraw 확인 화면의 가스 추정 콜백. 미주입 시 지갑 추정에 위임. */
  estimateGas?: EstimateGasFn;
  /**
   * 포트폴리오 금액 표시 통화의 USD 환산 비율(USD 기준). 미주입 시 1(=USD).
   */
  conversionRatio?: number;
  /** 포트폴리오 금액 표시 통화 기호. 미주입 시 `"$"`. */
  currencySymbol?: string;
  children: React.ReactNode;
}

function WalletPortfolioRoot({
  env,
  theme = "light",
  walletAddress,
  open: propOpen,
  onOpenChange,
  onOutlink,
  lpBalanceReader,
  stakingRewardsReader,
  sendTransaction,
  estimateGas,
  conversionRatio,
  currencySymbol,
  children,
}: WalletPortfolioProps) {
  const resolvedEnv = resolveEnvironment(env);
  const isControlled = propOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? propOpen : internalOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = React.useCallback(
    () => handleOpenChange(false),
    [handleOpenChange],
  );

  const contextValue = React.useMemo(
    () => ({
      env: resolvedEnv,
      theme,
      open,
      walletAddress,
      close,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      sendTransaction,
      estimateGas,
      conversionRatio,
      currencySymbol,
    }),
    [
      resolvedEnv,
      theme,
      open,
      walletAddress,
      close,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      sendTransaction,
      estimateGas,
      conversionRatio,
      currencySymbol,
    ],
  );

  return (
    <WalletPortfolioContext.Provider value={contextValue}>
      <Drawer.Root
        direction="bottom"
        open={open}
        onOpenChange={handleOpenChange}
        noBodyStyles
      >
        {children}
      </Drawer.Root>
    </WalletPortfolioContext.Provider>
  );
}

export const WalletPortfolio = Object.assign(WalletPortfolioRoot, {
  Trigger: WalletPortfolioTrigger,
  Content: WalletPortfolioContent,
});
